import { StatusPill } from '@/components/Rows'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import type { WhiteningActionId, WhiteningCard } from '@/features/whitening/state'
import { CardGroundProvider, useDiscColor, useGroundColor } from '@/ui/core/card-ground'
import { Header } from '@/ui/core/header'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'
import { CardSlot } from './CardSlot'

/** The card's padding. The same 12 the kit card carries, so the two stack as one family. */
const CARD_PAD = 12
/** What the words are set in past the card's own padding, matching the kit card's text block. */
const TEXT_PAD = 14

/**
 * The refill, once there is a kit to refill.
 *
 * No photograph. There is no photograph of a jar of gel worth 200 points, and borrowing the
 * kit's would say the wrong thing twice — this card is about the one consumable in the box, not
 * about the box. What stands in for it is the mark on a disc of whatever colour the card is not,
 * which is how every row in this app introduces a subject, at the size a card deserves.
 *
 * Otherwise it is the kit card's grammar exactly: pill top right, eyebrow, title, and the same
 * forty-six point slot at the foot. One line of copy where the kit card has a ledger, because a
 * refill has one fact and the kit has three.
 *
 * Blush, so the two cards are told apart before either is read. Two white cards were two of the
 * same object, one with a picture and one without, which made the refill look like an
 * afterthought rather than the second product — and this way the live card is the warmer one:
 * the finished kit is white, the thing you can actually do is pink. It rhymes with the tile pair
 * on Home, where blush and white already stand side by side. Only this card moves; both cards
 * blush would make the page pink on pink on pink, with the coral picture the only thing left
 * carrying an edge.
 *
 * The ground is declared once, here, and everything on the card reads its own colour off it —
 * the fill, the disc, the slot's panel. Two components because a provider cannot be its own
 * consumer: this one says what the ground is and the body asks.
 */
export const GelCard = (props: {
	card: WhiteningCard
	onAction: (action: WhiteningActionId) => void
}) => (
	<CardGroundProvider ground="blush">
		<GelCardBody {...props} />
	</CardGroundProvider>
)

const GelCardBody = ({
	card,
	onAction,
}: {
	card: WhiteningCard
	onAction: (action: WhiteningActionId) => void
}) => {
	const ground = useGroundColor()
	const disc = useDiscColor()

	return (
		<View className="rounded-note" style={{ padding: CARD_PAD, backgroundColor: ground }}>
			<View style={{ padding: TEXT_PAD }}>
				<VStack gap={20}>
					{/* The disc sets this row's height, not the pill: forty-two points against the
					    pill's twenty-two, and the row takes the taller of the two. Which is what
					    lets the corner stand empty — the heads-up state has no pill, because there
					    is no refill yet whose whereabouts a pill could report — without the card
					    losing twenty points and everything under it moving up. If the disc's
					    height ever stops being fixed, that stops being true. */}
					<View className="flex-row items-start justify-between">
						<View
							className="h-12 w-12 items-center justify-center rounded-full"
							style={{ backgroundColor: disc }}
						>
							{/* The drop, which is this app's mark for care — the gel is the thing that
						    does the work, and the sparkle next door belongs to the kit as a whole. */}
							<Glyph name="drop" size={24} color={colors.ink} />
						</View>
						{card.pill && (
							<StatusPill
								status={card.pill.status}
								label={card.pill.label}
								glyph={card.pill.glyph}
								neutral
								// White rather than the grey, which is what this pill already does when
								// it stands on the page's own ground: a grey pill on a pink card would
								// be a third neutral for no reason.
								onGround
							/>
						)}
					</View>

					<VStack gap={6}>
						<Text size="body3" tone="soft" className="tracking-[0.08em]">
							{card.eyebrow}
						</Text>
						<Header
							variant="h3"
							className="uppercase"
							style={{ fontSize: 28, lineHeight: 27 }}
						>
							{card.title}
						</Header>
						{!!card.line && (
							<Text size="body1" tone="soft" className="pt-1">
								{card.line}
							</Text>
						)}
					</VStack>

					<CardSlot slot={card.slot} onAction={onAction} />
				</VStack>
			</View>
		</View>
	)
}
